import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { tasksAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';

/**
 * TimeLogPanel
 * Props: taskId (string), task (object — passed from TaskDetail for status + estimated_hours)
 */
export default function TimeLogPanel({ taskId, task }) {
  const { user, isAdminOrAbove } = useRole();

  const [logs,        setLogs]        = useState([]);
  const [totalHours,  setTotalHours]  = useState(0);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [submitting,  setSubmitting]  = useState(false);

  // Add-form state
  const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
  const [logDate,      setLogDate]      = useState(todayStr);
  const [hours,        setHours]        = useState('');
  const [description,  setDescription]  = useState('');

  // Inline-edit state
  const [editingId,      setEditingId]      = useState(null);
  const [editDate,       setEditDate]       = useState('');
  const [editHours,      setEditHours]      = useState('');
  const [editDescription, setEditDescription] = useState('');

  const isBlocked = ['Done', 'Cancelled'].includes(task?.status);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res  = await tasksAPI.getTimeLogs(taskId);
      const data = res.data?.data;
      setLogs(data?.logs || []);
      setTotalHours(data?.total_hours ?? 0);
    } catch {
      // silently fail
    } finally {
      setLoadingLogs(false);
    }
  }, [taskId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // ── Add time log ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const hrs = Number(hours);
    if (!hrs || hrs < 0.25) { toast.error('Minimum 0.25 hours'); return; }
    setSubmitting(true);
    try {
      await tasksAPI.logTime(taskId, {
        logged_date: logDate,
        hours:       hrs,
        description: description.trim() || undefined,
      });
      toast.success('Time logged');
      setHours('');
      setDescription('');
      setLogDate(todayStr);
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to log time');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit helpers ────────────────────────────────────────────────────────────
  const startEdit = (log) => {
    setEditingId(log._id);
    setEditDate(log.logged_date ? new Date(log.logged_date).toLocaleDateString('en-CA') : todayStr);
    setEditHours(String(log.hours));
    setEditDescription(log.description || '');
  };
  const cancelEdit = () => { setEditingId(null); setEditDate(''); setEditHours(''); setEditDescription(''); };

  const saveEdit = async (logId) => {
    try {
      await tasksAPI.updateTimeLog(taskId, logId, {
        logged_date: editDate,
        hours:       Number(editHours),
        description: editDescription.trim() || undefined,
      });
      toast.success('Time log updated');
      cancelEdit();
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed');
    }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm('Delete this time log?')) return;
    try {
      await tasksAPI.deleteTimeLog(taskId, logId);
      toast.success('Time log deleted');
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Delete failed');
    }
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const estimated   = task?.estimated_hours;
  const overEstimate = estimated && totalHours > estimated;
  const pct         = estimated ? Math.min((totalHours / estimated) * 100, 100) : 0;

  // ── Styles ──────────────────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 7,
    border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };

  return (
    <div>
      {/* ── Total hours summary ─────────────────────────────────────────────── */}
      <div style={{
        background: overEstimate ? '#fff7ed' : '#f0fdf4',
        borderRadius: 10, padding: '14px 18px', marginBottom: 22,
        border: `1.5px solid ${overEstimate ? '#fed7aa' : '#bbf7d0'}`,
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: overEstimate ? '#ea580c' : '#16a34a' }}>
            {Number(totalHours).toFixed(2)} hrs
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
            Total Logged{estimated ? ` of ${estimated} estimated hrs` : ''}
          </div>
        </div>
        {estimated && (
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: overEstimate ? '#ea580c' : '#16a34a',
                borderRadius: 4, transition: 'width 0.3s',
              }} />
            </div>
            {overEstimate && (
              <div style={{ fontSize: 11, color: '#ea580c', fontWeight: 600, marginTop: 4 }}>
                ⚠️ Over estimate by {(totalHours - estimated).toFixed(2)} hrs
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Add log form (blocked if Done / Cancelled) ──────────────────────── */}
      {isBlocked ? (
        <div style={{
          background: '#f8fafc', borderRadius: 10,
          border: '1.5px solid #e2e8f0', padding: '22px',
          textAlign: 'center', marginBottom: 22,
        }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>🔒</div>
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
            Time logging is disabled for tasks with status "{task?.status}".
          </div>
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: 10,
          border: '1.5px solid #e2e8f0', padding: '18px 20px',
          marginBottom: 22, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 18 }}>⏱️</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#1B2A6B' }}>Log Time</span>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Date</label>
                <input type="date" style={inputStyle} value={logDate}
                  onChange={e => setLogDate(e.target.value)} required />
              </div>
              <div>
                <label style={labelStyle}>Hours</label>
                <input
                  type="number" min="0.25" max="16" step="0.25"
                  style={inputStyle} placeholder="e.g. 1.5"
                  value={hours} onChange={e => setHours(e.target.value)} required
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                Description{' '}
                <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
              </label>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                placeholder="What did you work on?"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>

            <button type="submit" disabled={submitting}
              style={{
                padding: '9px 22px', borderRadius: 8, border: 'none',
                background: submitting ? '#94a3b8' : '#1B2A6B',
                color: '#fff', fontWeight: 700, fontSize: 13,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}>
              {submitting ? 'Logging…' : 'Log Time'}
            </button>
          </form>
        </div>
      )}

      {/* ── Log list ───────────────────────────────────────────────────────── */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 12 }}>
          Time Logs{!loadingLogs && ` (${logs.length})`}
        </div>

        {loadingLogs ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading logs…</div>
        ) : logs.length === 0 ? (
          <div style={{
            color: '#94a3b8', fontSize: 13,
            background: '#f8fafc', borderRadius: 8,
            padding: '28px 16px', textAlign: 'center',
          }}>
            No time logs yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {logs.map(log => {
              const isOwner  = String(log.user_id?._id || log.user_id) === String(user?._id);
              const canEdit   = isOwner;
              const canDelete = isOwner || isAdminOrAbove;
              const isEditing = editingId === log._id;
              const logDateObj = log.logged_date ? new Date(log.logged_date) : null;

              return (
                <div key={log._id} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', border: '1px solid #f1f5f9' }}>
                  {isEditing ? (
                    /* ── Inline edit form ── */
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div>
                          <label style={labelStyle}>Date</label>
                          <input type="date" style={inputStyle} value={editDate}
                            onChange={e => setEditDate(e.target.value)} />
                        </div>
                        <div>
                          <label style={labelStyle}>Hours</label>
                          <input type="number" min="0.25" max="16" step="0.25"
                            style={inputStyle} value={editHours}
                            onChange={e => setEditHours(e.target.value)} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={labelStyle}>Description</label>
                        <textarea
                          style={{ ...inputStyle, resize: 'vertical', minHeight: 52 }}
                          value={editDescription}
                          onChange={e => setEditDescription(e.target.value)}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(log._id)}
                          style={{ padding: '6px 16px', borderRadius: 7, border: 'none', background: '#1B2A6B', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                          Save
                        </button>
                        <button onClick={cancelEdit}
                          style={{ padding: '6px 14px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Read-only row ── */
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: log.description ? 4 : 0 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: '#1B2A6B' }}>
                            {logDateObj
                              ? logDateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '—'}
                          </span>
                          <span style={{ background: '#dbeafe', color: '#2563eb', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
                            {log.hours}h
                          </span>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>by {log.user_id?.name || '—'}</span>
                        </div>
                        {log.description && (
                          <div style={{ fontSize: 12, color: '#475569', marginTop: 3, lineHeight: 1.5 }}>
                            {log.description}
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        {canEdit && (
                          <button onClick={() => startEdit(log)}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#475569', fontWeight: 600 }}>
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => deleteLog(log._id)}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fee2e2', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
